// Interprets Shopify's cartBuyerIdentityUpdate payload for the checkout's
// login / logout flows. Shopify reports a rejected update through
// `userErrors` with a 200 response, so a payload with no cart and no thrown
// error used to be treated as "nothing happened": the form cleared the
// customer while the cart kept its customer token and club pricing.

export type BuyerIdentityUpdatePayload = {
  cart?: { id: string } | null;
  userErrors?: Array<{ field?: string[] | null; message: string }> | null;
} | null | undefined;

export class BuyerIdentityUpdateError extends Error {
  readonly userErrors: Array<{ field?: string[] | null; message: string }>;
  constructor(message: string, userErrors: Array<{ field?: string[] | null; message: string }> = []) {
    super(message);
    this.name = "BuyerIdentityUpdateError";
    this.userErrors = userErrors;
  }
}

/** Returns the updated cart or throws a BuyerIdentityUpdateError. */
export function assertBuyerIdentityUpdated<T extends BuyerIdentityUpdatePayload>(
  payload: T,
  action: "logout" | "login" = "logout",
): NonNullable<NonNullable<T>["cart"]> {
  const userErrors = payload?.userErrors ?? [];
  if (userErrors.length) {
    throw new BuyerIdentityUpdateError(
      `Shopify rejected the ${action}: ${userErrors.map((e) => e.message).join("; ")}`,
      userErrors,
    );
  }
  if (!payload?.cart) {
    throw new BuyerIdentityUpdateError(`Shopify returned no cart after the ${action}`);
  }
  return payload.cart as NonNullable<NonNullable<T>["cart"]>;
}

/** Copy shown next to the Logout button when the detach fails. */
export function describeLogoutFailure(error: unknown): string {
  const detail = error instanceof Error ? error.message : String(error ?? "");
  const network = /fetch|network|Failed to fetch|timeout|502|503|504/i.test(detail);
  return network
    ? "We couldn't log you out because the connection dropped. Please check your connection and try again."
    : "We couldn't log you out just now. Please try again; your cart and prices are unchanged.";
}
